import {
    Directive,
    ElementRef,
    forwardRef,
    HostListener,
    Renderer2
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';


/**
 * The accessor for writing a value and listening to changes on a date input element.
 * As a fallback for older browsers, input in the format of 'dd/mm/yyyy' is parsed to Date.
 *
 *  ### Example
 *  `<input type="date" name="myBirthday" ngModel useValueAsDate>`
 */
@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: '[useValueAsDate]',
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateValueAccessorDirective),
            multi: true
        }, {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DateValueAccessorDirective),
            multi: true
        }],
    standalone: false
})
export class DateValueAccessorDirective implements ControlValueAccessor, Validator {

    // both empty functions are placeholders that need to be defined as empty funcs so they can receive a valid implementation later on.
    // eslint-disable-next-line no-empty
    @HostListener('input', ['$event.target.value']) public onChange = (value: string) => { };
    // eslint-disable-next-line no-empty
    @HostListener('blur', []) public onTouched = () => { };

    constructor(private _renderer: Renderer2, private _elementRef: ElementRef) { }

    public writeValue(value: Date | null | undefined): void {
        // From model (Date) → view (string)

        if (!value) {
            this._elementRef.nativeElement.value = '';
            return;
        }

        const yyyy = value.getFullYear();
        const mm = String(value.getMonth() + 1).padStart(2, '0');
        const dd = String(value.getDate()).padStart(2, '0');

        this._elementRef.nativeElement.value = `${yyyy}-${mm}-${dd}`;
    }

    public registerOnChange(fn: (value: any) => void): void {
        this.onChange = (value: string) => {
            const date = new Date(`${value}T00:00:00.000`);
            if (!isNaN(date.getTime())) {
                fn(date);
            } else {
                fn(null);
            }
        };
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', isDisabled);
    }

    // function is required by the interface but does nothing in our case here.
    // eslint-disable-next-line no-empty
    public registerOnValidatorChange(fn: () => void): void {
    }

    public validate(c: AbstractControl): ValidationErrors | null {
        if (!c.value || (c.value instanceof Date && !isNaN(c.value.getTime()))) {
            return null;
        }
        const date = new Date(c.value);
        return !isNaN(date.getTime()) ? null : { date: true };
    }
}
